import { emiteEventoDelEmbudo } from 'lib/analitica_del_embudo'

// Los seis eventos del wizard de personalización (PRD-134-1, TC-134-1.2.3.4). Dos pasos desde que se
// quitó el del perfil (2026-10-20): `event_interest` es el 1 y `newsletter_topics` el 2.
//
// Implementan el contrato del catálogo (`docs/analytics/onboarding-events.md`, TC-134-1.4.7.1), que
// es la fuente de verdad de los nombres, los parámetros y los valores válidos. Aquí no se construye
// contexto ni se toca `dataLayer`: `emiteEventoDelEmbudo` adjunta las propiedades comunes que el
// servidor publicó en el `<head>` y empuja, igual que en la analítica de `/registro`.
//
// Dos niveles bajo el mismo prefijo, y NO son excluyentes:
//
//   paso      `welcome_step_viewed`, `welcome_step_completed`, `welcome_step_skipped`
//   pregunta  `welcome_step_question_answered`, `welcome_step_question_skipped` (paso de eventos)
//
// Continuar sin marcar nada completa el paso Y deja la pregunta sin contestar, así que los dos
// niveles no se suman: `welcome_step_question_skipped` incluye a `welcome_step_skipped`.
//
// La conducta vive en este módulo y no en el controlador Stimulus por lo mismo que en
// `analitica_del_registro`: lo que hay que llevar —qué paso está abierto y cuándo se vio— es de la
// SESIÓN DE PÁGINA, no de un elemento del DOM.

export const EVENTO_VISTO = 'welcome_step_viewed'
export const EVENTO_COMPLETADO = 'welcome_step_completed'
export const EVENTO_SALTADO = 'welcome_step_skipped'
export const EVENTO_CATEGORIAS = 'welcome_categories_selected'
export const EVENTO_PREGUNTA_RESPONDIDA = 'welcome_step_question_answered'
export const EVENTO_PREGUNTA_SALTADA = 'welcome_step_question_skipped'

// `skip_method` tiene un catálogo CERRADO de un solo valor: no existe ningún otro control de salto
// ("Saltar cuestionario" no está en el diseño, decisión Diseño + Producto 2026-05-20). Se emite
// igualmente, y no se da por implícito, para que el día que aparezca un segundo método los datos
// anteriores queden atribuidos en vez de en blanco.
export const METODO_DE_SALTO = 'skip_step'

// El paso abierto, o `null`. Mientras no lo haya, todo lo de abajo es no-op: la analítica nunca
// rompe el flujo que mide.
//
// Un paso abierto se RESUELVE una sola vez —completándolo o saltándolo— y a partir de ahí tampoco
// emite: cada `submit` de la página llega aquí, y quien pulsa "Continuar" otra vez mientras carga la
// página siguiente manda un segundo `submit` de la misma vista. Sin esto el paso se contaba
// completado dos o tres veces (TC-134-1.4.7.2: 3 de 19 personas en la validación de staging), y la
// repetición heredaba en la capa de datos los parámetros del evento anterior. Volver a ver el paso
// —"← Atrás", o recuperar la página del historial— lo abre de nuevo y vuelve a contar.
let paso = null

// Abre el paso y emite `welcome_step_viewed`. Arranca además el cronómetro de `duration_ms`, que
// se reinicia con cada carga del paso —"← Atrás" vuelve a cargarlo—, así que el tiempo total de un
// paso es la suma de sus tramos y no la `duration_ms` del último.
export function abreElPasoDelWizard ({ stepNumber, stepName, question = null, valorConjunto = null } = {}) {
  if (!stepNumber || !stepName) return null

  paso = {
    numero: stepNumber,
    nombre: stepName,
    pregunta: question,
    conjunto: valorConjunto,
    inicio: Date.now(),
    resuelto: false
  }

  emiteEventoDelEmbudo(EVENTO_VISTO, { step_number: paso.numero, step_name: paso.nombre })

  return paso
}

export function cierraElPasoDelWizard () {
  paso = null
}

// "Continuar". `valores` son las respuestas marcadas de la pregunta del paso, en el orden del
// catálogo; `categorias`, los ids de las temáticas marcadas en el paso de newsletters.
export function emitePasoCompletado ({ valores = [], categorias = [] } = {}) {
  if (!resuelveElPaso()) return false

  const respuesta = respuestaDeclarada(valores)

  emiteEventoDelEmbudo(EVENTO_COMPLETADO, {
    step_number: paso.numero,
    step_name: paso.nombre,
    // `payload` se OMITE cuando no hubo respuesta, en vez de emitirse vacío: una cadena vacía no se
    // distingue de un parámetro que la guarda de PII se llevó por delante. El paso de newsletters
    // tampoco lo lleva nunca — su respuesta viaja en `welcome_categories_selected`.
    ...(respuesta ? { payload: respuesta } : {}),
    duration_ms: Date.now() - paso.inicio
  })

  return paso.pregunta ? emiteLaPregunta(respuesta) : emiteLasCategorias(categorias)
}

// "Saltar paso". No persiste nada, así que no hay respuesta que transportar; y no lleva
// `duration_ms` porque mediría cuánto se tarda en no contestar.
export function emitePasoSaltado () {
  if (!resuelveElPaso()) return false

  emiteEventoDelEmbudo(EVENTO_SALTADO, {
    step_number: paso.numero,
    step_name: paso.nombre,
    skip_method: METODO_DE_SALTO
  })

  if (!paso.pregunta) return true

  return emiteEventoDelEmbudo(EVENTO_PREGUNTA_SALTADA, {
    step_name: paso.nombre,
    question: paso.pregunta
  })
}

// Marca el paso abierto como resuelto. Devuelve `false` —y quien llama no emite— si no hay paso
// abierto o si esta vista ya se resolvió.
function resuelveElPaso () {
  if (!paso || paso.resuelto) return false

  paso.resuelto = true

  return true
}

// La respuesta del paso en un solo parámetro de texto: un parámetro de evento de GA4 es escalar.
//
// Y la resume igual que el servidor (`OpcionDeEventos.interes`): las dos casillas marcadas a la vez
// se guardan como un solo valor, `both`, y no como la lista. Sin esto el `payload` diría algo
// distinto de lo que se ha guardado, que es justo lo que el evento existe para poder leer.
function respuestaDeclarada (valores) {
  const marcados = [...valores]
  if (paso.conjunto && marcados.length > 1) return paso.conjunto

  return marcados.join(',')
}

// Nivel de pregunta del paso de eventos. Es lo que hace legible el KR "al menos 1 pregunta
// respondida", que a nivel de paso no se puede leer.
//
// No lleva `step_number`: `step_name` lo determina uno a uno, y el evento de paso que sale en el
// mismo acto ya trae los dos.
function emiteLaPregunta (respuesta) {
  if (!respuesta) {
    return emiteEventoDelEmbudo(EVENTO_PREGUNTA_SALTADA, {
      step_name: paso.nombre,
      question: paso.pregunta
    })
  }

  return emiteEventoDelEmbudo(EVENTO_PREGUNTA_RESPONDIDA, {
    step_name: paso.nombre,
    question: paso.pregunta,
    value: respuesta
  })
}

// La respuesta del paso de newsletters, en su evento propio y en el mismo acto que el completado del paso.
//
// Se emite también con `count: 0`: continuar sin marcar ninguna temática es un camino legítimo
// (Gherkin de `US-134-1.2.6`) y no es lo mismo que saltar el paso. `category_ids` sí se omite
// entonces, por el mismo motivo que `payload`: una cadena vacía no se distingue de un parámetro que
// la guarda se llevó por delante, y "no marcó ninguna" ya lo dice `count`.
//
// Los ids van en orden ascendente y serializados por comas —`count` no es redundante: GA4 recorta
// los valores a 100 caracteres, así que contar por la longitud de la cadena daría un número falso—.
function emiteLasCategorias (categorias) {
  const ids = [...categorias].map(Number).filter(Number.isInteger).sort((uno, otro) => uno - otro)

  return emiteEventoDelEmbudo(EVENTO_CATEGORIAS, {
    count: ids.length,
    ...(ids.length > 0 ? { category_ids: ids.join(',') } : {})
  })
}
